/**
 * Definition Card — „Was ist X?"-Begriffskarte.
 *
 * Klare Erklär-Karte für einen Begriff: kleines Label (z. B. „BEGRIFF"), der
 * Begriff groß, ein wachsender Akzent-Unterstrich und darunter die Definition.
 * Die Glas-Karte skaliert rein, der Begriff erscheint, der Unterstrich
 * zeichnet sich, die Definition blendet auf. Ideal um Konzepte zu erklären.
 *
 * Inputs:
 *   text     – der Begriff (z. B. „Allyver")
 *   subtext  – die Definition / Erklärung
 *   extras.eyebrow – Label über dem Begriff (Default „BEGRIFF")
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const definitionCard: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";

  const term = inputs.text ?? "Allyver";
  const definition =
    inputs.subtext ??
    "Das digitale Einkaufszentrum deiner Region – Restaurants, Shops, Services & Events lokaler Anbieter, gebündelt in einer App.";
  const label = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "BEGRIFF";

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const termPx = isVertical ? lg * 1.04 : lg * 0.96;
  const defPx = isVertical ? md * 0.6 : md * 0.54;
  const labelPx = sm * 0.62;

  const wrapId = `${clipPrefix}-wrap`;
  const cardId = `${clipPrefix}-card`;
  const labelId = `${clipPrefix}-label`;
  const termId = `${clipPrefix}-term`;
  const lineId = `${clipPrefix}-line`;
  const defId = `${clipPrefix}-def`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:${isVertical ? "120px 80px" : "110px 160px"};overflow:hidden;">
    <div id="${cardId}" style="position:relative;display:flex;flex-direction:column;gap:${isVertical ? 22 : 20}px;width:100%;max-width:${isVertical ? 940 : 1180}px;padding:${isVertical ? "56px 56px" : "60px 70px"};border-radius:40px;background:linear-gradient(155deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.bg, 0.6)} 100%);border:1px solid ${withAlpha(accent, 0.3)};box-shadow:0 40px 100px rgba(0,0,0,0.45), inset 0 1px 0 ${withAlpha("#ffffff", 0.18)};will-change:transform,opacity;backdrop-filter:blur(8px);">
      <div id="${labelId}" style="display:inline-flex;align-items:center;gap:12px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${labelPx}px;letter-spacing:6px;text-transform:uppercase;color:${accent};"><span style="width:30px;height:2px;background:${accent};"></span><span>${escapeHtml(label)}</span></div>
      <div id="${termId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${termPx}px;line-height:1.0;letter-spacing:-3px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(term)}</div>
      <div id="${lineId}" style="height:5px;width:100%;border-radius:3px;transform-origin:0% 50%;background:linear-gradient(90deg, ${accent} 0%, ${withAlpha(themeColors.secondary, 0.9)} 100%);box-shadow:0 0 24px ${withAlpha(accent, 0.5)};"></div>
      <div id="${defId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${defPx}px;line-height:1.36;letter-spacing:-0.2px;color:${withAlpha(themeColors.fg, 0.8)};text-wrap:balance;">${escapeHtml(definition)}</div>
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${cardId}`, { opacity: 0, y: 40, scale: 0.94 }),
    gsapSetLine(`#${labelId}`, { opacity: 0, y: -6 }),
    gsapSetLine(`#${termId}`, { opacity: 0, y: 18 }),
    gsapSetLine(`#${lineId}`, { scaleX: 0 }),
    gsapSetLine(`#${defId}`, { opacity: 0, y: 14 }),
  ].join("\n");

  const tweens: string[] = [
    t.to(`#${cardId}`, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.5)" }, 0.1),
    t.to(`#${labelId}`, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 0.32),
    t.to(`#${termId}`, { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" }, 0.44),
    t.to(`#${lineId}`, { scaleX: 1, duration: 0.55, ease: "power2.inOut" }, 0.6),
    t.to(`#${defId}`, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, 0.78),
  ];

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
